import { createRouter, createWebHistory } from "vue-router";
import { pcRoutes, h5Routes } from "./routes";
import { commonRoutes } from "./commonRoutes";
import { addPageBrowserRecord } from "@/api/user";
import store from "@/store";
import useWeChatSdk from "@/hooks/useWeChatSdk";
import useSocket from "@/hooks/useSocket";
import {
  isWechatBrowser,
  setTitle,
  getCustomerServiceQrCode,
} from "@/utils/help.js";

// 根据屏幕宽度来加载对应的路由
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: store.state.app.isMobile
    ? h5Routes.concat(commonRoutes)
    : pcRoutes.concat(commonRoutes),
});

const whiteLists = [
  "/login",
  "/privacy",
  "/auth",
  "/mhlp/login",
  "/retrieve",
  "/wechatauth",
  "/platformwechatauth",
  "/meetwechatauth",
  "/error/502",
  "/patienteducationdetail",
  "/caseList",
  "/caseHome",
  "/score/memberday",
  "/assistedconsultationhome3", //内嵌到医生端，不需要token
  "/fileView", //文件预览
  "/scoreplay", //评分视频
  "/emailFeedback", //邮件反馈页（收件人免登录）
  "/emailView", //邮件备份页（收件人免登录）
];

const noWechatAuthLists = [
  "/auth",
  "/wechatauth",
  "/platformwechatauth",
  "/marketingwechatauth",
  "/meetingwebwechatauth",
  "/meetwechatauth",
  "/eyewechatauth",
  "/error/502",
  "/fileView", //文件预览
  "/scoreplay",
  "/emailFeedback", //邮件反馈页（收件人免微信授权）
  "/emailView", //邮件备份页（收件人免微信授权）
];

const noSocketNameLists = ["PilotPlay", "NewPilotPlay"];

// 是否是微信浏览器
const _isWechatBrowser = isWechatBrowser();

// 记录浏览器参数
function addHistory(linkKey, linkUrl) {
  let params = {
    linkKey,
    linkUrl,
  };
  addPageBrowserRecord(params);
}

router.beforeEach(async (to, from, next) => {
  // localStorage.setItem("token", "1a438c3f-23de-4f9b-89a0-5abe6fba3e66");
  const token = localStorage.getItem("token");

  // 是大会页面
  if (
    store.state.app.isMobile &&
    [
      "/meet/index",
      "/meet/pay",
      "/meet/manypay",
      "/meet/order",
      "/meet/manyorder",
    ].includes(to.path)
  ) {
    let liveMeetingId = to.query.liveMeetingId; //大会id
    if (liveMeetingId) {
      const meetInfo = store.getters["user/getMeetInfo"];
      // 获取大会配置
      if (!meetInfo) {
        try {
          const { appId, id } = await store.dispatch("user/getMeetInfoAction", {
            id: to.query.liveMeetingId,
          });
          // 是微信浏览器并且不是刷新页面
          if (_isWechatBrowser) {
            const wechat_redirect = encodeURIComponent(
              `${
                process.env.VUE_APP_WECHAT_REDIRECT
              }?_back_url=${encodeURIComponent(location.href)}`
            );
            return window.location.replace(
              `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${wechat_redirect}&response_type=code&scope=snsapi_userinfo&state=${id}#wechat_redirect`
            );
          }
        } catch (error) {
          next();
        }
      }
    }
  }

  // 简版个人中心参数
  if (to.query.isSimple) {
    sessionStorage.setItem("isSimple", true);
  }

  if (token) {
    const userInfo = store.getters["user/getUserInfo"];
    //H5
    if (store.state.app.isMobile) {
      useWeChatSdk();
      // 当去首页，或者没有用户信息的时候 重新获取用户信息
      if (
        (!userInfo &&
          to.path !== "/auth" &&
          to.path !== "/wechatauth" &&
          to.path !== "/platformwechatauth" &&
          to.path !== "/marketingwechatauth" &&
          to.path !== "/meetwechatauth") ||
        to.path === "/index"
      ) {
        try {
          const userInfo = await store.dispatch("user/getUserInfoAction");
          // 有用户信息后连接 websocket
          if (!noSocketNameLists.includes(to.name)) {
            useSocket(userInfo);
          }
          next();
        } catch (error) {
          next();
        }
      } else {
        next();
      }
    } else {
      //PC
      if (
        (!userInfo && to.path !== "/auth" && to.path !== "/login") ||
        // 去认证页每次获取最新用户信息
        to.name == "RenZheng" ||
        to.name == "ForceAuth"
      ) {
        const results = await store.dispatch("user/getUserInfoAction");
        // 白名单页面（邮件反馈页/备份页等）即使是登录账号未绑手机也放行
        if (
          !results.phone &&
          to.path !== "/bind" &&
          !whiteLists.includes(to.path)
        ) {
          // 记住路径
          let _return_url = encodeURIComponent(location.href);
          if (from.path === "/login") {
            _return_url = from?.query._return_url;
          }
          next({
            name: "BindPhone",
            query: {
              _return_url,
            },
          });
        } else {
          if (to.query?._monitor_) {
            addHistory(to.query._monitor_, location.href);
          }
          next();
        }
      } else {
        next();
      }
    }
  } else {
    // 在微信浏览器
    if (_isWechatBrowser) {
      // 是auth页面直接进
      if (noWechatAuthLists.includes(to.path)) {
        next();
      } else {
        // 如果直接在统一个人中心通过儿科的几个页面授权，我们需要模拟带上erke来源
        if (to.path === "/mhlpbindphone") {
          to.query._big_origin = "BASE";
          sessionStorage.setItem("_big_origin", to.query._big_origin);
        }
        to.query._return_url = encodeURIComponent(location.href);

        // 统一个人中心单独授权 也会判断是否有 _big_origin 有则按对应来源授权 THEME_PM、NERVE、THEME_MS、EYE
        const source = await getCustomerServiceQrCode(to.query._big_origin);
        let APP_ID = "VUE_APP_" + source + "_APP_ID";
        const appId = process.env[APP_ID]; // 动态id
        const urlParam = new URLSearchParams(to.query);
        const wechat_redirect = encodeURIComponent(
          `${process.env.VUE_APP_REDIRECT_URL}?${urlParam.toString()}`
        );
        window.location = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${wechat_redirect}&response_type=code&scope=snsapi_userinfo&state=${source}#wechat_redirect`;
      }
      // 不是微信浏览器
    } else {
      // 是白名单直接进
      if (whiteLists.includes(to.path)) {
        next();
      } else {
        // 记住路径
        const _return_url = encodeURIComponent(location.href);
        // 去登录页面
        next({
          name: "Login",
          query: {
            _return_url,
          },
        });
      }
    }
  }
});

router.afterEach(async (to) => {
  // 设置页面title
  if (to.meta?.title) {
    setTitle(to.meta.title);
  }
  // 存储页面信息 key为 _monitor_
  if (!noWechatAuthLists.includes(to.path)) {
    if (to.query?._monitor_) {
      addHistory(to.query._monitor_, location.href);
    } else if (to.meta?.record) {
      addHistory(to.meta?.record, location.href);
    }
  }
});

export default router;
